// QUÉ CAMPOS TIENE CADA MODO, y cómo se le cuentan al agente —
// ADR-2026-09-10-el-agente-toca-la-pantalla y
// ADR-2026-09-10-el-conocimiento-de-los-modos-vive-con-el-codigo.
//
// Este archivo es el catálogo de campos que el chat puede ver y tocar en cada modo:
//   • `AYUDA_DE_CAMPOS`: qué es cada campo, dicho como se le explicaría al usuario.
//   • `CAMPOS_POR_MODO`: qué campos tiene cada modo, en el orden de la pantalla.
//   • `QUE_HACE_EL_MODO`: para qué sirve cada modo, en una línea.
//   • `describirModo(datos)`: la ficha viva (valores actuales + opciones) que se arma
//     desde el estado de `InterfazV2`, para los modos que se abren en su sheet.
//   • `describirAnimar` / `describirCarrusel`: lo mismo para los modos con pantalla propia.
//
// ES LA FUENTE DE VERDAD que mantiene sincronizado lo que el agente sabe con lo que la
// app hace. Cuando un modo cambia (un campo nuevo, un tope, una opción que se va), se
// toca ACÁ, la pantalla que registra el controlador, y la skill del modo — y hay tests
// que fallan si la skill se olvida (`camposDeModo.test.ts`). Ver la sección del CLAUDE.md.
//
// Puro: los tipos de selección se importan sólo como tipos.

import type { AnuncioFieldState } from '../panels/AnuncioPanel';
import type { VoiceGuionValue } from '../components/VoiceGuionSection';
import type { FondoSelection } from '../FondoPicker';
import type { ModelSelection } from '../ModelPicker';
import { FORMAT_OPTIONS_V2 } from '../mockData';
import { COPY_MODOS, esDesignModelo, MODE_KIND_TO_PATH } from '../modesCatalog';
import { palabraDeModeloActual } from '../../../lib/nombreDeModelo';
// Qué modos se configuran en una sola pantalla: lo que el agente le cuenta al usuario
// sobre el paso tiene que decir lo mismo que hace la pantalla
// (ADR-2026-09-10-el-paso-de-cada-modo-se-decide-en-un-solo-lugar).
import { esModoDeUnaSolaPantalla } from '../pasoDeModo';
import { creditosPorImagenTexto, OPCIONES_RESOLUCION, RESOLUCIONES } from '../../../lib/resolucionImagen';
import type {
  CampoDePantalla,
  DescripcionDePantalla,
  OpcionDeCampo,
} from '../../../chat-atlas/puenteDePantalla';

export type CampoId =
  | 'productos'
  | 'sin_producto'
  | 'modelo'
  | 'poses'
  | 'fondo'
  | 'fondo_descripcion'
  | 'vistas'
  | 'formato'
  | 'resolucion'
  | 'cantidad'
  | 'instruccion'
  | 'titulo'
  | 'sin_titulo'
  | 'descripcion'
  | 'sin_descripcion'
  | 'cta'
  | 'sin_cta'
  | 'colores'
  | 'duracion'
  | 'multi_escena'
  | 'gancho'
  | 'voz'
  | 'forma_de_hablar'
  | 'guion'
  | 'subtitulos'
  | 'imagen'
  | 'descripcion_video'
  | 'video_referencia'
  | 'imagenes_referencia'
  | 'indicaciones';

/** Qué es cada campo, con el nombre que tiene en pantalla. */
export const AYUDA_DE_CAMPOS: Record<CampoId, { etiqueta: string; queEs: string }> = {
  productos: {
    etiqueta: 'Productos',
    queEs:
      'Los productos del catálogo con los que se genera: el resultado copia el producto real de sus fotos. Se eligen en "Elegir de mis productos" o subiendo una foto nueva.',
  },
  sin_producto: {
    etiqueta: 'Sin producto',
    queEs: 'Generar sin un producto: el influencer habla de lo que diga el guion.',
  },
  modelo: {
    // El campo se llama "Modelo" en Moda e Indumentaria y "Avatar" en el resto de los
    // rubros (ADR-2026-09-22-en-moda-el-modelo-se-llama-modelo). Son `get`
    // porque esta tabla se arma al cargar el módulo y el agente la lee en cada turno:
    // así la palabra que el agente usa es la que el usuario está viendo en pantalla.
    get etiqueta() {
      return palabraDeModeloActual().titulo;
    },
    get queEs() {
      const p = palabraDeModeloActual();
      return `Quién aparece en la foto. "Automático" deja que la IA elija una persona acorde a la marca; un ${p.singular} guardado sale siempre igual (cara y cuerpo de su foto y de su ficha de referencia).`;
    },
  },
  poses: {
    etiqueta: 'Pose',
    get queEs() {
      return `La postura y el encuadre. Sin ninguna ("Automática") la decide la IA; con varias sale una imagen por pose. Una pose de 2 o 3 personas pide esa cantidad de ${palabraDeModeloActual().plural}.`;
    },
  },
  fondo: {
    etiqueta: 'Fondo',
    queEs:
      'El escenario. "Automático" lo decide la IA según el producto; "Fondo blanco" es el de catálogo, y el liso también viene en beige, rosa y negro; un fondo de la biblioteca o uno propio se usa como referencia del lugar, no se calca.',
  },
  fondo_descripcion: {
    etiqueta: 'Describir fondo',
    queEs: 'Un fondo dicho en palabras ("una terraza con luz de tarde"). Reemplaza al elegido en Fondo.',
  },
  vistas: {
    etiqueta: 'Vistas',
    queEs:
      'Qué tomas del producto se generan (frente, espalda, detalle, sobre una superficie…). Sale una imagen por vista y por producto.',
  },
  formato: {
    etiqueta: 'Formato',
    queEs:
      'La proporción de la imagen: Posteo 4:5, Historias/Reels 9:16, Cuadrado 1:1, Retrato 2:3, Banner web 16:9, Editorial 3:2, Billboard 21:9.',
  },
  resolucion: {
    etiqueta: 'Calidad',
    queEs:
      'La resolución de salida: Estándar 2K (la de siempre, 1 crédito por imagen), Liviana 1K (mismo costo, archivo más chico) y Máxima 4K (2 créditos por imagen). Para redes alcanza la Estándar.',
  },
  cantidad: {
    etiqueta: 'Cantidad de imágenes',
    queEs: 'Cuántas imágenes distintas salen en la tanda; cada una cuesta 1 crédito.',
  },
  instruccion: {
    etiqueta: 'Instrucción adicional',
    queEs:
      'Texto libre para lo que los selectores no cubren: luz, actitud, hora del día, un detalle del encuadre. Corto y concreto, sin repetir lo ya elegido.',
  },
  titulo: {
    etiqueta: 'Título',
    queEs: 'El texto principal del anuncio. Vacío = lo escribe la IA a partir del producto.',
  },
  sin_titulo: { etiqueta: 'Sin título', queEs: 'Sacar el título del anuncio.' },
  descripcion: {
    etiqueta: 'Descripción',
    queEs: 'La bajada del anuncio, una o dos líneas. Vacío = la escribe la IA.',
  },
  sin_descripcion: { etiqueta: 'Sin descripción', queEs: 'Sacar la descripción del anuncio.' },
  cta: {
    etiqueta: 'Botón',
    queEs: 'El llamado a la acción ("Comprá ahora", "Ver más"). Vacío = lo escribe la IA.',
  },
  sin_cta: { etiqueta: 'Sin botón', queEs: 'Sacar el botón del anuncio.' },
  colores: {
    etiqueta: 'Colores',
    queEs: 'Hasta 4 colores del anuncio en hex (#1644EB), separados por coma. Vacío = la paleta de la marca.', // hex-de-marca-ok: ejemplo de hex en un texto
  },
  duracion: {
    etiqueta: 'Duración',
    queEs: 'Segundos del video. Cada segundo cuesta 1 crédito (el doble con múltiples escenas).',
  },
  multi_escena: {
    etiqueta: 'Múltiples escenas',
    queEs: 'Corta el video en varias escenas con transiciones. Cobra el doble.',
  },
  gancho: {
    etiqueta: 'Gancho',
    queEs: 'Cómo arranca el video para retener en los primeros segundos: un gancho del catálogo, o sin gancho.',
  },
  voz: { etiqueta: 'Voz', queEs: 'La voz del influencer. Es obligatoria.' },
  forma_de_hablar: {
    etiqueta: 'Forma de hablar',
    queEs: 'Cómo actúa la voz: tono, ritmo, energía ("entusiasta y rápida", "calma y cercana").',
  },
  guion: {
    etiqueta: 'Guion',
    queEs: 'Lo que dice el influencer, textual. Vacío con producto = lo escribe la IA mirando la foto.',
  },
  subtitulos: { etiqueta: 'Subtítulos', queEs: 'Subtítulos incrustados en el video.' },
  imagen: {
    etiqueta: 'Imagen',
    queEs: 'La foto de origen: una generada en Atlas (de Creaciones) o una subida.',
  },
  descripcion_video: {
    etiqueta: 'Descripción del video',
    queEs: 'Qué movimiento tiene que tener: cámara, gesto, luz. La IA propone una; se puede editar.',
  },
  video_referencia: {
    etiqueta: 'Video de referencia',
    queEs: 'El video del que se copia el movimiento: uno propio (de 4 a 29 segundos) o el de una plantilla de Atlas (10 segundos).',
  },
  imagenes_referencia: {
    etiqueta: 'Imágenes',
    queEs: 'Hasta 6: la persona, el producto o la prenda que van en el video, cada una con qué es (Persona, Producto, Prenda u Otro).',
  },
  indicaciones: {
    etiqueta: 'Indicaciones',
    queEs: 'Opcional, pero es lo que más ayuda: qué tiene que cambiar y qué no ("que lleve puesta la campera", "el fondo queda igual").',
  },
};

/**
 * Los campos de cada modo, en el orden de la pantalla. `plantilla` cubre las plantillas
 * (kinds `template` / `design` / `design-producto`), que abren en el mismo sheet.
 */
export const CAMPOS_POR_MODO: Record<string, CampoId[]> = {
  'design-modelo': ['productos', 'modelo', 'poses', 'fondo', 'fondo_descripcion', 'formato', 'resolucion', 'instruccion'],
  'design-auto': ['productos', 'cantidad', 'formato', 'instruccion'],
  'fotos-producto': ['productos', 'vistas', 'fondo', 'fondo_descripcion', 'formato', 'instruccion'],
  anuncio: ['productos', 'titulo', 'sin_titulo', 'descripcion', 'sin_descripcion', 'cta', 'sin_cta', 'colores', 'cantidad', 'formato', 'instruccion'],
  'catalogo-ia': ['productos', 'modelo', 'fondo', 'fondo_descripcion', 'resolucion'],
  infografia: ['productos', 'resolucion'],
  'video-influencer': ['productos', 'sin_producto', 'gancho', 'duracion', 'multi_escena', 'voz', 'forma_de_hablar', 'guion', 'subtitulos'],
  plantilla: ['productos', 'formato', 'resolucion', 'instruccion'],
  'animar-imagen': ['imagen', 'descripcion_video', 'duracion', 'multi_escena'],
  carrusel: ['imagen', 'cantidad'],
  'recrear-video': ['video_referencia', 'imagenes_referencia', 'indicaciones'],
};

/** Para qué sirve cada modo, en una línea para el usuario. */
export const QUE_HACE_EL_MODO: Record<string, string> = {
  redes: 'Imágenes y videos para redes desde un prompt: un interruptor Imagen/Video/Asistente, producto y modelo opcionales, y lo generado cae en un lienzo. La imagen genera como Prompt libre y el video como el Creador de video; en Asistente el pedido le llega al Agente.',
  get 'design-modelo'() {
    const p = palabraDeModeloActual();
    return `Fotos de tus productos puestos en un ${p.singular} IA (automático o guardado), con pose, fondo y formato a elección.`;
  },
  'design-auto': 'La IA arma sola la escena para tu producto: elegís el producto, cuántas imágenes y el formato.',
  'fotos-producto': 'Tu producto solo, en varias vistas de catálogo (frente, detalle, sobre superficie…) con fondo blanco o descripto.',
  anuncio: 'Una pieza lista para publicar con tu producto, título, bajada, botón y colores de marca.',
  get 'catalogo-ia'() {
    const p = palabraDeModeloActual();
    return `Varias vistas de cada producto en una sola tanda (con ${p.singular} y sin ${p.singular}), como para armar el catálogo.`;
  },
  'catalogo-agente':
    'El catálogo armado con el agente, para Hogar y Decoración y los rubros que no son Moda ni Belleza (esos usan Generación en lote): la persona elige productos y cuenta qué quiere; el agente mira los productos, pide los datos que faltan, propone las tomas y un escritor redacta un prompt por producto y toma; se revisa en una grilla y se genera en lotes; después se pueden sumar vistas, reescribir prompts en masa y editar o rehacer en masa las fotos ya salidas. Sin campos: se trabaja con las herramientas de la pantalla y las skills catalogo-de-muebles (el método) y catalogo-hogar (qué toma pide cada producto) en Hogar, o catalogo-por-rubro en los otros rubros.',
  infografia: 'La ficha técnica visual de un producto: medidas y atributos sobre su foto.',
  'video-influencer': 'Un video corto con un influencer IA presentando tu producto, con voz, guion y gancho.',
  plantilla: 'Una escena ya armada para tu producto: elegís el producto, el formato, la calidad (en las de imagen) y una instrucción.',
  'animar-imagen': 'Convierte una foto (generada o subida) en un video corto con movimiento realista.',
  carrusel: 'De una foto, varias tomas nuevas del mismo producto (otros ángulos y encuadres, misma escena).',
  'recrear-video': 'Toma un video (uno propio o una plantilla de Atlas) y lo vuelve a hacer con la persona, el producto o la ropa de tus imágenes, con el mismo movimiento y la misma cámara. Atlas mira el video y decide solo qué cambiar; borra marcas de agua y lo entrega en 1080. Vive dentro de Recrear, con el interruptor en Video.',
  'videos-cortos':
    'Un video vertical de ~15 s por producto (hasta 50), estilo Mercado Libre, planeado con el agente: la persona elige productos y toca Adelante; el director escribe el guion de cada uno en su tarjeta, el agente elige la voz en off por producto (las voces del Estudio) y Crear lanza los videos a 12 créditos cada uno, de Pro para arriba. Sin campos: herramientas de la pantalla y la skill videos-cortos.',
  get asistente() {
    return `Un chat para pedir contenido desde cero: imágenes, videos y audio, con producto y ${palabraDeModeloActual().singular} adjuntos.`;
  },
  get 'prompt-libre'() {
    return `Describís la imagen y Atlas la genera, con producto, ${palabraDeModeloActual().singular} o foto de referencia opcionales.`;
  },
  'creador-video':
    'Describís el video (o elegís un tipo: reseña, unboxing, producto héroe, lifestyle…) y Atlas escribe el prompt y lo genera, con referencias, formato, calidad, resolución (en Profesional, 480p es Borrador: después se pasa a 1080p) y duración.',
  'shorts-studio': 'Subís un video hablando a cámara y lo transforma entero en un estilo (claymation, cómic…).',
  'variantes-acabado': 'El mismo mueble en cada acabado (material o color): de una foto de partida, una imagen por acabado. Sólo para Hogar y Decoración.',
  'ambiente-completo': 'De 2 a 4 muebles de la marca en UNA sola foto de ambiente (un living armado), en vez de packshots sueltos. El ambiente puede ser uno de los cinco curados, uno descrito a mano, o una foto propia del lugar que el usuario sube (queda en "Mis fondos", los mismos de Modelo). Sólo para Hogar y Decoración.',
  'mostrar-como-funciona': 'Un video corto (5 a 8 s) de un mueble en uso: alguien se sienta, la cámara recorre el ambiente, el sofá cama se abre. Sólo para Hogar y Decoración.',
  'mostrar-como-funciona-belleza': 'Un video corto (5 a 8 s) de un producto de belleza en uso, a partir de una imagen: qué pasa, dónde y con qué indicaciones. Sólo para Belleza y Cuidado Personal.',
  get 'replicar-foto'() {
    const p = palabraDeModeloActual();
    return `Elegís una foto que hace de plantilla (tuya, subida o un pin de tu Pinterest) y Atlas la vuelve a hacer con los cambios que pidas. Productos y ${p.singular} son opcionales: podés poner hasta 4 productos y todos entran en la misma foto; con ${p.singular}, esa persona reemplaza a la de la foto. Sirve para cualquier rubro.`;
  },
  'diseno-indumentaria': 'Para diseñadores de ropa: partís de un plano técnico o describís la prenda y, en un lienzo, la mostrás en uso (una persona real, con un prompt escrito por foto), la mezclás con texturas e inspiraciones y sacás su plano técnico. Sólo para Moda e Indumentaria.',
  'asistente-v2': 'La variante rediseñada del chat de "Crear desde cero": el mismo chat de generación, sin el switch Imagen/Video ni el desplegable de Ayuda de IA.',
};

/** Los kinds de plantilla, que comparten el sheet y la misma ficha. */
export const KINDS_DE_PLANTILLA = new Set(['template', 'design', 'design-producto']);

/** El nombre de la pantalla: "el modo Modelo", "la plantilla «Verano»". */
export function nombreDePantalla(kind: string, plantillaTitulo?: string): string {
  if (KINDS_DE_PLANTILLA.has(kind)) return plantillaTitulo ? `la plantilla «${plantillaTitulo}»` : 'una plantilla';
  const path = MODE_KIND_TO_PATH[kind];
  const titulo = path ? COPY_MODOS[path]?.titulo : undefined;
  return titulo ? `el modo ${titulo}` : `el modo ${kind}`;
}

// ── Topes ────────────────────────────────────────────────────────────────────

export const TOPES = {
  /**
   * Productos por modo. Modelo arma un outfit; Fotos de producto y Catálogo son tandas.
   *
   * Es el tope POR KIND, y el kind no siempre alcanza: una plantilla acepta 6 productos
   * en Moda y **uno** en Belleza
   * (ADR-2026-09-14-la-plantilla-de-belleza-se-genera-con-un-producto), y las dos son
   * `plantilla`. Cuando la pantalla sabe otro tope lo manda en `topeProductos` y ése
   * gana — así el agente nunca ofrece sumar un producto donde el picker no deja.
   */
  productos: { 'videos-cortos': 50, 'design-modelo': 6, 'design-auto': 6, 'fotos-producto': 12, 'catalogo-ia': 12, 'catalogo-agente': 100, infografia: 12, anuncio: 3, 'video-influencer': 1, plantilla: 6 } as Record<string, number>,
  /** Poses por tanda: cada una es una foto más. */
  poses: 6,
  instruccion: 800,
  textoAnuncio: 120,
  colores: 4,
  formaDeHablar: 300,
  guion: 1500,
  descripcionVideo: 800,
  fondoDescripcion: 400,
} as const;

// ── La ficha viva de un modo del sheet de InterfazV2 ─────────────────────────

export interface DatosDelModo {
  kind: string;
  ruta: string;
  /** El `panelStep` de InterfazV2. */
  paso: string;
  plantillaTitulo?: string;
  productos: { id: string; name: string }[];
  /**
   * El tope REAL de productos de esta pantalla, cuando no es el de `TOPES.productos`
   * (la plantilla de Belleza, que genera con uno solo). Sin dato manda la tabla.
   */
  topeProductos?: number;
  /** Todo el catálogo: es la lista contra la que se valida. */
  catalogo: { id: string; name: string }[];
  sinProducto: boolean;
  modelo: ModelSelection | null;
  modelos: { id: string | number; nombre: string }[];
  poses: string[];
  /** Las compatibles con la cantidad de modelos (lo que la grilla muestra). */
  posesDisponibles: { id: string; nombre: string; cantidadPersonas?: number }[];
  fondo: FondoSelection | null;
  fondosBiblioteca: { id: number; nombre: string; categoria: string | null }[];
  fondosGuardados: { fileName: string; nombre: string }[];
  formato: string | null;
  resolucion: string;
  /**
   * La plantilla abierta elige Calidad (`plantillaEligeCalidad`): las de imagen
   * (ADR-2026-09-28-las-plantillas-eligen-la-calidad) y las de Fondo blanco
   * (ADR-2026-10-12-fondo-blanco-como-las-plantillas). En una plantilla sin este dato la
   * ficha no muestra la Calidad, que es lo que hacía antes. Los demás modos no la miran.
   */
  eligeCalidad?: boolean;
  instruccion: string;
  cantidad: number;
  cantidadMax: number;
  vistas: string[];
  vistasDisponibles: { id: string; nombre: string; productIds: string[] }[];
  anuncio: {
    titulo: AnuncioFieldState;
    descripcion: AnuncioFieldState;
    cta: AnuncioFieldState;
    colores: string[];
    coloresMarca: string[];
  };
  video: {
    duracion: number;
    min: number;
    max: number;
    multiEscena: boolean;
    ganchoId: string | null;
    ganchos: { id: string; nombre: string }[];
    /** null hasta que carguen; ahí el campo es de solo lectura. */
    ganchosCargados: boolean;
    voz: VoiceGuionValue;
    voces: { nombre: string; estilo: string | null; sexo: string | null }[];
    vocesCargadas: boolean;
  };
  /** El costo con lo puesto, si la pantalla lo sabe. */
  costo?: string;
}

const base = (id: CampoId): Pick<CampoDePantalla, 'id' | 'etiqueta' | 'queEs'> => ({
  id,
  etiqueta: AYUDA_DE_CAMPOS[id].etiqueta,
  queEs: AYUDA_DE_CAMPOS[id].queEs,
});

const texto = (id: CampoId, valor: string, max: number, nota?: string): CampoDePantalla => ({
  ...base(id),
  tipo: 'texto',
  valor,
  valorLegible: valor ? `"${valor.length > 100 ? `${valor.slice(0, 97)}…` : valor}"` : '(vacío)',
  max,
  editable: true,
  nota,
});

const booleano = (id: CampoId, valor: boolean, nota?: string): CampoDePantalla => ({
  ...base(id),
  tipo: 'booleano',
  valor,
  valorLegible: valor ? 'sí' : 'no',
  editable: true,
  nota,
});

function campoProductos(d: DatosDelModo, kindDeTope: string): CampoDePantalla {
  // El de la pantalla primero: es el que de verdad está aplicando el picker.
  const max = d.topeProductos ?? TOPES.productos[kindDeTope] ?? 6;
  const elegidos = d.productos.map((p) => p.name);
  return {
    ...base('productos'),
    tipo: 'opciones',
    valor: d.productos.map((p) => p.id),
    valorLegible:
      elegidos.length > 0 ? elegidos.join(', ') : d.sinProducto ? 'sin producto (a propósito)' : 'ninguno todavía',
    opciones: d.catalogo.map((p) => ({ id: p.id, etiqueta: p.name })),
    opcionesEnElPrompt: 'los ids de la lista PRODUCTOS del prompt (o su nombre exacto)',
    min: 1,
    max,
    editable: true,
    // …y sólo donde eso es cierto: los modos de una sola pantalla no avanzan a ningún
    // lado (ADR-2026-09-10-el-paso-de-cada-modo-se-decide-en-un-solo-lugar), así que ahí
    // la nota prometía un paso que no existe.
    nota:
      !esModoDeUnaSolaPantalla(d.kind) &&
      (d.paso === 'choose-product' || d.paso === 'pick-product')
        ? 'Al ponerlos, la pantalla pasa sola al paso de configurar.'
        : undefined,
  };
}

function legibleDeModelo(m: ModelSelection | null): string {
  if (!m) return 'ninguno (hay que elegir uno)';
  if (m.mode === 'auto') return 'Automático (lo elige la IA)';
  if (m.mode === 'preset') return m.name;
  const p = palabraDeModeloActual();
  if (m.mode === 'custom') return `un ${p.singular} descripto a mano en pantalla`;
  return `una foto de ${p.singular} subida en pantalla`;
}

function campoModelo(d: DatosDelModo): CampoDePantalla {
  const opciones: OpcionDeCampo[] = [
    { id: 'auto', etiqueta: 'Automático', nota: 'lo elige la IA' },
    ...d.modelos.map((m) => ({ id: String(m.id), etiqueta: m.nombre })),
  ];
  const valor = !d.modelo ? null : d.modelo.mode === 'auto' ? 'auto' : d.modelo.mode === 'preset' ? d.modelo.id : null;
  return {
    ...base('modelo'),
    tipo: 'opcion',
    valor,
    valorLegible: legibleDeModelo(d.modelo),
    opciones,
    editable: true,
    nota: `Crear un ${palabraDeModeloActual().singular} nuevo o subir la foto de uno se hace en pantalla ("Creá tu ${palabraDeModeloActual().singular}": elige las características, le mostramos tres opciones, se queda con una y es gratis).`,
  };
}

function campoPoses(d: DatosDelModo): CampoDePantalla {
  const porId = new Map(d.posesDisponibles.map((p) => [p.id, p]));
  const nombres = d.poses.map((id) => porId.get(id)?.nombre ?? id);
  return {
    ...base('poses'),
    tipo: 'opciones',
    valor: d.poses,
    valorLegible: nombres.length > 0 ? `${nombres.join(', ')} (${nombres.length} ${nombres.length === 1 ? 'pose' : 'poses'})` : 'Automática (ninguna elegida)',
    opciones: d.posesDisponibles.map((p) => ({
      id: p.id,
      etiqueta: p.nombre,
      nota: p.cantidadPersonas && p.cantidadPersonas > 1 ? `${p.cantidadPersonas} personas` : undefined,
    })),
    min: 0,
    max: TOPES.poses,
    editable: true,
    nota: 'Lista vacía = Automática. Con más de una pose la pantalla prende sola "Utilizar varias poses".',
  };
}

function legibleDeFondo(f: FondoSelection | null, kind: string): string {
  if (!f) return kind === 'fotos-producto' ? 'Fondo liso blanco' : 'Automático';
  switch (f.mode) {
    case 'auto':
      return 'Automático (lo decide la IA)';
    case 'blanco':
      return f.colorLiso && f.colorLiso !== 'blanco' ? `Fondo liso ${f.colorLiso}` : 'Fondo blanco';
    case 'biblioteca':
    case 'guardado':
      return `"${f.name}"`;
    case 'descripcion':
      return `descripto: "${f.text}"`;
  }
}

/**
 * ¿La generación de este modo mira `colorLiso`?
 *
 * `fotos-producto` lo tiene en su propia rama; Modelo y Catálogo IA pasan por
 * `backgroundDeFondo`. `catalogo-muebles` NO: su fondo sale de la vista
 * (`backgroundParaVistaMueble`) y la selección entera se descarta, así que ofrecerle
 * colores sería dejar que el agente confirme algo que no va a pasar.
 */
export const modoUsaColorLiso = (kind: string | null | undefined): boolean =>
  kind === 'design-modelo' || kind === 'catalogo-ia' || kind === 'fotos-producto';

function campoFondo(d: DatosDelModo): CampoDePantalla {
  const esFotosProducto = d.kind === 'fotos-producto';
  const opciones: OpcionDeCampo[] = esFotosProducto
    ? [
        { id: 'blanco', etiqueta: 'Fondo liso blanco' },
        { id: 'beige', etiqueta: 'Fondo liso beige' },
        { id: 'rosa', etiqueta: 'Fondo liso rosa' },
        { id: 'negro', etiqueta: 'Fondo liso negro' },
      ]
    : [
        { id: 'auto', etiqueta: 'Automático', nota: 'lo decide la IA' },
        { id: 'blanco', etiqueta: 'Fondo blanco' },
        // El liso con color llegó a Modelo/Catálogo
        // (ADR-2026-09-15-el-fondo-liso-con-color-llega-a-modelo). Con prefijo para no
        // chocar con los ids de biblioteca/guardado, que también son compuestos.
        //
        // SÓLO en los modos cuya generación lo consume. En `catalogo-muebles` el fondo se
        // DERIVA de la vista (`backgroundParaVistaMueble`) y la selección no se mira: si
        // se ofrecieran acá, el agente confirmaría un color que la generación ignora.
        ...(modoUsaColorLiso(d.kind)
          ? [
              { id: 'liso:beige', etiqueta: 'Fondo liso beige' },
              { id: 'liso:rosa', etiqueta: 'Fondo liso rosa' },
              { id: 'liso:negro', etiqueta: 'Fondo liso negro' },
            ]
          : []),
        ...d.fondosGuardados.map((f) => ({ id: `guardado:${f.fileName}`, etiqueta: f.nombre, nota: 'propio' })),
        ...d.fondosBiblioteca.map((f) => ({
          id: `biblioteca:${f.id}`,
          etiqueta: f.nombre,
          nota: f.categoria ?? undefined,
        })),
      ];
  const f = d.fondo;
  let valor: string | null = null;
  if (!f) valor = esFotosProducto ? 'blanco' : 'auto';
  else if (f.mode === 'auto') valor = 'auto';
  else if (f.mode === 'blanco') {
    const color = f.colorLiso ?? 'blanco';
    valor = esFotosProducto ? color : color === 'blanco' ? 'blanco' : `liso:${color}`;
  }
  else if (f.mode === 'biblioteca') valor = `biblioteca:${f.id}`;
  else if (f.mode === 'guardado') valor = `guardado:${f.fileName}`;
  return {
    ...base('fondo'),
    tipo: 'opcion',
    valor,
    valorLegible: legibleDeFondo(f, d.kind),
    opciones,
    editable: true,
    nota: 'Para un fondo en palabras usá fondo_descripcion. Subir una foto de fondo se hace en pantalla.',
  };
}

function campoFondoDescripcion(d: DatosDelModo): CampoDePantalla {
  const actual = d.fondo?.mode === 'descripcion' ? d.fondo.text : '';
  return texto('fondo_descripcion', actual, TOPES.fondoDescripcion, 'Al ponerlo, reemplaza al Fondo elegido.');
}

function campoVistas(d: DatosDelModo): CampoDePantalla {
  const porId = new Map(d.vistasDisponibles.map((v) => [v.id, v]));
  const nombres = d.vistas.map((id) => porId.get(id)?.nombre ?? id);
  return {
    ...base('vistas'),
    tipo: 'opciones',
    valor: d.vistas,
    valorLegible: nombres.length > 0 ? nombres.join(', ') : 'ninguna (hay que elegir al menos una)',
    opciones: d.vistasDisponibles.map((v) => ({
      id: v.id,
      etiqueta: v.nombre,
      nota: d.productos.length > 1 ? `aplica a ${v.productIds.length} de los productos` : undefined,
    })),
    min: 1,
    editable: true,
    nota: 'Las vistas dependen de los productos elegidos: cambiá los productos primero.',
  };
}

function campoFormato(d: DatosDelModo): CampoDePantalla {
  const actual = FORMAT_OPTIONS_V2.find((f) => f.id === d.formato);
  return {
    ...base('formato'),
    tipo: 'opcion',
    valor: d.formato,
    valorLegible: actual ? actual.name : 'el último usado (por defecto Cuadrado 1:1)',
    opciones: FORMAT_OPTIONS_V2.map((f) => ({ id: f.id, etiqueta: f.name })),
    editable: true,
  };
}

function campoResolucion(d: DatosDelModo): CampoDePantalla {
  const actual = OPCIONES_RESOLUCION.find((o) => o.id === d.resolucion);
  return {
    ...base('resolucion'),
    tipo: 'opcion',
    valor: d.resolucion,
    valorLegible: actual ? `${actual.label}` : d.resolucion,
    opciones: OPCIONES_RESOLUCION.map((o) => ({
      id: o.id,
      etiqueta: o.label,
      nota: creditosPorImagenTexto(o.id),
    })),
    editable: true,
  };
}

function campoCantidad(d: DatosDelModo): CampoDePantalla {
  return {
    ...base('cantidad'),
    tipo: 'numero',
    valor: d.cantidad,
    valorLegible: String(d.cantidad),
    min: 1,
    max: d.cantidadMax,
    entero: true,
    editable: true,
  };
}

function camposDeAnuncio(d: DatosDelModo): CampoDePantalla[] {
  const a = d.anuncio;
  const t = (id: CampoId, s: AnuncioFieldState) =>
    texto(id, s.value, TOPES.textoAnuncio, s.hidden ? 'Está oculto: al escribirlo se vuelve a mostrar.' : undefined);
  return [
    t('titulo', a.titulo),
    booleano('sin_titulo', a.titulo.hidden),
    t('descripcion', a.descripcion),
    booleano('sin_descripcion', a.descripcion.hidden),
    t('cta', a.cta),
    booleano('sin_cta', a.cta.hidden),
    {
      ...base('colores'),
      tipo: 'texto',
      valor: a.colores.join(', '),
      valorLegible:
        a.colores.length > 0 ? a.colores.join(', ') : a.coloresMarca.length > 0 ? `los de la marca (${a.coloresMarca.slice(0, TOPES.colores).join(', ')})` : '(vacío)',
      max: 60,
      editable: true,
      nota: `Hasta ${TOPES.colores} colores hex separados por coma; vacío vuelve a la paleta de la marca.`,
    },
  ];
}

function camposDeVideo(d: DatosDelModo): CampoDePantalla[] {
  const v = d.video;
  const gancho: CampoDePantalla = {
    ...base('gancho'),
    tipo: 'opcion',
    valor: v.ganchoId,
    valorLegible: v.ganchoId
      ? (v.ganchos.find((g) => g.id === v.ganchoId)?.nombre ?? (v.ganchoId === 'sin-gancho' ? 'Sin gancho' : v.ganchoId))
      : 'ninguno elegido',
    opciones: [{ id: 'sin-gancho', etiqueta: 'Sin gancho' }, ...v.ganchos.map((g) => ({ id: g.id, etiqueta: g.nombre }))],
    editable: v.ganchosCargados,
    motivoNoEditable: v.ganchosCargados ? undefined : 'los ganchos todavía están cargando; volvé a llamar ver_pantalla.',
    nota: 'Sin gancho, la duración máxima baja a 14 segundos.',
  };
  const voz: CampoDePantalla = {
    ...base('voz'),
    tipo: 'opcion',
    valor: v.voz.vozId,
    valorLegible: v.voz.vozNombre ?? v.voz.vozId ?? 'ninguna (obligatoria)',
    opciones: v.voces.map((x) => ({
      id: x.nombre,
      etiqueta: x.nombre,
      nota: [x.sexo, x.estilo].filter(Boolean).join(', ') || undefined,
    })),
    editable: v.vocesCargadas,
    motivoNoEditable: v.vocesCargadas ? undefined : 'las voces todavía están cargando; volvé a llamar ver_pantalla.',
  };
  const guion: CampoDePantalla = {
    ...texto('guion', v.voz.guionMode === 'texto' ? v.voz.guionTexto : '', TOPES.guion),
    valorLegible:
      v.voz.guionMode === 'audio'
        ? 'un audio grabado en pantalla'
        : v.voz.guionMode === 'auto'
          ? 'Automático (lo escribe la IA desde la foto del producto)'
          : v.voz.guionTexto
            ? `"${v.voz.guionTexto.length > 100 ? `${v.voz.guionTexto.slice(0, 97)}…` : v.voz.guionTexto}"`
            : '(vacío)',
    nota: 'Al escribirlo, el guion pasa a "Escribir". Para grabar un audio se hace en pantalla.',
  };
  return [
    gancho,
    {
      ...base('duracion'),
      tipo: 'numero',
      valor: v.duracion,
      valorLegible: `${v.duracion} segundos`,
      min: v.min,
      max: v.max,
      entero: true,
      editable: true,
    },
    booleano('multi_escena', v.multiEscena),
    voz,
    texto('forma_de_hablar', v.voz.formaDeHablar, TOPES.formaDeHablar),
    guion,
    booleano('subtitulos', v.voz.subtitulos),
  ];
}

const PASOS: Record<string, string> = {
  preview: 'mirando la portada del modo',
  'choose-product': 'elegir el producto',
  'pick-product': 'elegir el producto',
  'upload-product': 'subir un producto',
  'select-vistas': 'elegir las vistas',
  configure: 'configurar',
};

/**
 * Cómo se le nombra el paso al agente.
 *
 * Los modos de UNA SOLA PANTALLA se quedan en `choose-product` de punta a punta
 * (ADR-2026-09-10-el-paso-de-cada-modo-se-decide-en-un-solo-lugar): ahí ese paso no es
 * "elegir el producto" sino la pantalla entera —productos, modelo, pose, fondo y el
 * Generar—, y decirle al agente que está eligiendo el producto le hace prometer un paso
 * siguiente que no existe.
 */
function nombreDelPaso(kind: string | null | undefined, paso: string): string {
  if (esModoDeUnaSolaPantalla(kind) && paso === 'choose-product') {
    return 'la pantalla del modo (se configura y se genera todo acá)';
  }
  return PASOS[paso] ?? paso;
}

/** Qué falta para poder generar, con lo que hay puesto. */
export function faltaParaGenerarEnModo(d: DatosDelModo): string[] {
  const falta: string[] = [];
  const kind = d.kind;
  if (d.productos.length === 0 && !(kind === 'video-influencer' && d.sinProducto)) {
    falta.push('elegir al menos un producto');
  }
  if (esDesignModelo(kind) && !d.modelo)
    falta.push(`elegir un ${palabraDeModeloActual().singular} (o Automático)`);
  if (kind === 'fotos-producto' && d.vistas.length === 0) falta.push('elegir al menos una vista');
  if (kind === 'video-influencer') {
    if (!d.video.voz.vozId) falta.push('elegir una voz');
    if (d.sinProducto && d.video.voz.guionMode === 'texto' && !d.video.voz.guionTexto.trim()) {
      falta.push('escribir el guion (sin producto no hay de qué hablar)');
    }
  }
  return falta;
}

/** La ficha de un modo del sheet de InterfazV2, con lo que hay puesto. */
export function describirModo(d: DatosDelModo): DescripcionDePantalla {
  const kind = d.kind;
  const clave = KINDS_DE_PLANTILLA.has(kind) ? 'plantilla' : kind;
  const ids = CAMPOS_POR_MODO[clave] ?? [];
  const campos: CampoDePantalla[] = [];
  for (const id of ids) {
    switch (id) {
      case 'productos':
        campos.push(campoProductos(d, clave));
        break;
      case 'sin_producto':
        campos.push(booleano('sin_producto', d.sinProducto, 'Al prenderlo se sueltan los productos y el guion pasa a "Escribir".'));
        break;
      case 'modelo':
        campos.push(campoModelo(d));
        break;
      case 'poses':
        campos.push(campoPoses(d));
        break;
      case 'fondo':
        campos.push(campoFondo(d));
        break;
      case 'fondo_descripcion':
        campos.push(campoFondoDescripcion(d));
        break;
      case 'vistas':
        campos.push(campoVistas(d));
        break;
      case 'formato':
        campos.push(campoFormato(d));
        break;
      case 'resolucion':
        // En las plantillas, sólo las de imagen: la de video no tiene Calidad en pantalla.
        if (clave === 'plantilla' && !d.eligeCalidad) break;
        campos.push(campoResolucion(d));
        break;
      case 'cantidad':
        campos.push(campoCantidad(d));
        break;
      case 'instruccion':
        campos.push(texto('instruccion', d.instruccion, TOPES.instruccion));
        break;
      case 'titulo':
        campos.push(...camposDeAnuncio(d));
        break;
      case 'gancho':
        campos.push(...camposDeVideo(d));
        break;
      // Los de anuncio y video entran en bloque con su primer campo.
      case 'sin_titulo':
      case 'descripcion':
      case 'sin_descripcion':
      case 'cta':
      case 'sin_cta':
      case 'colores':
      case 'duracion':
      case 'multi_escena':
      case 'voz':
      case 'forma_de_hablar':
      case 'guion':
      case 'subtitulos':
      case 'imagen':
      case 'descripcion_video':
        break;
    }
  }
  const notas: string[] = [];
  if (kind === 'catalogo-ia' || kind === 'catalogo-muebles' || kind === 'infografia') {
    notas.push('Las vistas y tarjetas de la tanda se eligen en pantalla (no desde el chat).');
  }
  if (esDesignModelo(kind)) {
    notas.push(
      `Varios ${palabraDeModeloActual().plural} en la misma foto se prende en pantalla ("Utilizar varios ${palabraDeModeloActual().plural}").`,
    );
  }
  notas.push('Generar lo hace el usuario con el botón; vos dejás la pantalla lista y se lo decís.');
  return {
    pantalla: nombreDePantalla(kind, d.plantillaTitulo),
    ruta: d.ruta,
    queHace: QUE_HACE_EL_MODO[clave] ?? QUE_HACE_EL_MODO[kind] ?? '',
    paso: nombreDelPaso(kind, d.paso),
    campos,
    faltaParaGenerar: faltaParaGenerarEnModo(d),
    costo: d.costo,
    notas,
  };
}

// ── Modos con pantalla propia ────────────────────────────────────────────────

export interface DatosDeAnimar {
  ruta: string;
  paso: 'elegir' | 'configurar';
  imagenElegida: boolean;
  descripcion: string;
  duracion: number;
  min: number;
  max: number;
  multiEscena: boolean;
}

export function describirAnimar(d: DatosDeAnimar): DescripcionDePantalla {
  const costo = d.multiEscena ? d.duracion * 2 : d.duracion;
  return {
    pantalla: 'Animar imagen',
    ruta: d.ruta,
    queHace: QUE_HACE_EL_MODO['animar-imagen'],
    paso: d.paso === 'elegir' ? 'elegir la imagen' : 'configurar',
    campos: [
      {
        ...base('imagen'),
        tipo: 'booleano',
        valor: d.imagenElegida,
        valorLegible: d.imagenElegida ? 'elegida' : 'ninguna todavía',
        editable: false,
        motivoNoEditable: 'se elige tocándola en la grilla de Creaciones o subiéndola.',
      },
      texto('descripcion_video', d.descripcion, TOPES.descripcionVideo),
      {
        ...base('duracion'),
        tipo: 'numero',
        valor: d.duracion,
        valorLegible: `${d.duracion} segundos`,
        min: d.min,
        max: d.max,
        entero: true,
        editable: true,
      },
      booleano('multi_escena', d.multiEscena),
    ],
    faltaParaGenerar: d.imagenElegida ? [] : ['elegir la imagen de origen'],
    costo: `${costo} créditos (${d.duracion} s${d.multiEscena ? ' × 2 por múltiples escenas' : ''})`,
    notas: ['Requiere plan Pro o Ultra.', 'Generar lo hace el usuario con el botón.'],
  };
}

export interface DatosDeRecrearVideo {
  ruta: string;
  /** `null` = todavía no hay video puesto. */
  video: { segundos: number; deUnaPlantilla: string | null } | null;
  /** Qué es cada imagen puesta, en orden ("Persona", "Producto"). */
  imagenes: string[];
  maxImagenes: number;
  indicaciones: string;
  maxIndicaciones: number;
  /** Créditos con lo puesto, o `null` sin video. */
  costo: number | null;
  segundosDeSalida: number;
  plantillas: string[];
}

export function describirRecrearVideo(d: DatosDeRecrearVideo): DescripcionDePantalla {
  const falta: string[] = [];
  if (!d.video) falta.push('subir el video de referencia o elegir una plantilla');
  if (d.imagenes.length === 0) falta.push('sumar al menos una imagen');
  return {
    pantalla: 'Recrear video',
    ruta: d.ruta,
    queHace: QUE_HACE_EL_MODO['recrear-video'],
    paso: 'configurar',
    campos: [
      {
        ...base('video_referencia'),
        tipo: 'booleano',
        valor: !!d.video,
        valorLegible: d.video
          ? `${d.video.deUnaPlantilla ? `la plantilla «${d.video.deUnaPlantilla}»` : 'un video propio'} (${d.video.segundos} s)`
          : 'ninguno todavía',
        editable: false,
        motivoNoEditable: `se sube en pantalla o se elige una plantilla en la pestaña Plantillas (${d.plantillas.join(', ')}).`,
      },
      {
        ...base('imagenes_referencia'),
        tipo: 'texto',
        valor: d.imagenes.join(', '),
        valorLegible: d.imagenes.length > 0 ? `${d.imagenes.length}: ${d.imagenes.join(', ')}` : 'ninguna todavía',
        editable: false,
        motivoNoEditable: `se suben o se eligen del catálogo en pantalla (hasta ${d.maxImagenes}).`,
      },
      texto('indicaciones', d.indicaciones, d.maxIndicaciones),
    ],
    faltaParaGenerar: falta,
    ...(d.costo !== null
      ? { costo: `${d.costo} créditos (${d.segundosDeSalida} s de video, 3 por segundo; sale en 1080)` }
      : {}),
    notas: [
      'Es para todos los planes: cobra 3 créditos por segundo.',
      'Generar lo hace el usuario con el botón; tarda de 6 a 15 minutos y el resultado cae en la pestaña Historial.',
    ],
  };
}

export interface DatosDeCarrusel {
  ruta: string;
  paso: 'elegir' | 'configurar';
  imagenElegida: boolean;
  cantidad: number;
  min: number;
  max: number;
}

export function describirCarrusel(d: DatosDeCarrusel): DescripcionDePantalla {
  return {
    pantalla: 'Carrusel de imágenes',
    ruta: d.ruta,
    queHace: QUE_HACE_EL_MODO.carrusel,
    paso: d.paso === 'elegir' ? 'elegir la imagen' : 'configurar',
    campos: [
      {
        ...base('imagen'),
        tipo: 'booleano',
        valor: d.imagenElegida,
        valorLegible: d.imagenElegida ? 'elegida' : 'ninguna todavía',
        editable: false,
        motivoNoEditable: 'se elige tocándola en la grilla de Creaciones o subiéndola.',
      },
      {
        ...base('cantidad'),
        tipo: 'numero',
        valor: d.cantidad,
        valorLegible: String(d.cantidad),
        min: d.min,
        max: d.max,
        entero: true,
        editable: true,
      },
    ],
    faltaParaGenerar: d.imagenElegida ? [] : ['elegir la imagen de origen'],
    costo: `${d.cantidad} créditos (1 por imagen nueva)`,
    notas: ['Generar lo hace el usuario con el botón.'],
  };
}

/** Todos los kinds que la ficha conoce (para los tests de deriva). */
export const KINDS_CON_FICHA: string[] = Object.keys(CAMPOS_POR_MODO);

/** Sanidad: las resoluciones de la ficha son las de la app. */
export const RESOLUCIONES_DE_LA_FICHA: readonly string[] = RESOLUCIONES;
